import { useState } from "react";

import { Card, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import { Select } from "@/components/ui/Form.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { OVERRIDES, overrideValues, overridePatch } from "@/lib/userDetail.js";

/*
 * The six per-user desktop overrides, from the "Desktop App Overrides" card.
 *
 * Every field here is one User.settings key that userController.updateUser
 * actually accepts. That is not a coincidence to preserve casually: a select
 * for anything else would post a field the API drops, and the form would report
 * success while nothing changed on the device.
 *
 * Only CHANGED fields are sent — see overridePatch. Posting the whole settings
 * object would re-write values another admin may have changed in the meantime,
 * turning an open tab into a silent revert.
 */
export default function OverridesCard({ settings, organization, canEdit, saving, onSave }) {
    const saved = overrideValues(settings);

    // Keyed by the saved values, so a successful save (or someone else's change
    // arriving on refetch) resets the form to the server's truth rather than
    // leaving stale edits sitting on top of it.
    return <OverridesForm key={JSON.stringify(saved)} {...{ settings, organization, canEdit, saving, onSave }} />;
}

function OverridesForm({ settings, organization, canEdit, saving, onSave }) {
    const saved = overrideValues(settings);
    const [values, setValues] = useState(saved);

    const patch = overridePatch(values, settings);
    const dirty = Object.keys(patch).length > 0;

    return (
        <Card>
            <CardHead>
                <CardTitle>Desktop App Overrides</CardTitle>
                <CardExtra>
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                        Per-user · falls back to organization settings
                    </span>
                    {canEdit && (
                        <Button
                            variant="primary"
                            disabled={!dirty}
                            loading={saving}
                            onClick={() => onSave(patch)}
                            data-save-overrides
                        >
                            {dirty ? `Save ${Object.keys(patch).length} change${Object.keys(patch).length === 1 ? "" : "s"}` : "Save changes"}
                        </Button>
                    )}
                </CardExtra>
            </CardHead>

            <div className="flex flex-col">
                {OVERRIDES.map((field) => {
                    // A control the desktop would ignore is shown, disabled,
                    // with the reason — hiding it would leave an admin
                    // wondering where the setting went.
                    const blocked = field.entitlement && !organization?.[field.entitlement];
                    const inherited = values[field.key] === "inherit";

                    return (
                        <div
                            key={field.key}
                            data-override={field.key}
                            className="flex flex-wrap items-center gap-4 border-b border-slate-100 px-4.5 py-3.5 last:border-0 dark:border-slate-800"
                        >
                            <div className="min-w-56 flex-1">
                                <p className="flex flex-wrap items-center gap-2 text-[13px] font-medium">
                                    {field.label}
                                    <Tag tone={inherited ? "slate" : "brand"} data-override-state>
                                        {inherited ? "Inherited" : "Overridden"}
                                    </Tag>
                                    {blocked && <Tag tone="amber">Not available</Tag>}
                                </p>
                                <p className="mt-0.5 text-[11.5px] text-slate-400 dark:text-slate-500">
                                    {blocked ? field.entitlementNote : field.description}
                                </p>
                            </div>

                            <Select
                                className="w-60 shrink-0"
                                aria-label={field.label}
                                disabled={!canEdit || blocked}
                                value={values[field.key]}
                                onChange={(e) =>
                                    setValues((v) => ({ ...v, [field.key]: e.target.value }))
                                }
                            >
                                {field.options.map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}
